import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaWarehouse,
  FaList,
  FaEllipsisH,
  FaChartLine,
  FaBuilding,
  FaSeedling,
  FaQuestionCircle,
  FaCalendarAlt,
} from 'react-icons/fa';
import RationIcon from './icons/RationIcon';
import { useTranslation } from 'react-i18next';

interface Tab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  primary?: boolean;
}

interface MobileBottomTabBarProps {
  isFarmer: boolean;
  isCorporate: boolean;
  hasSelectedFarm?: boolean;
  isAdmin: boolean;
  isConsultant: boolean;
  onMorePress: () => void;
}

/**
 * 5-tab iOS bottom bar.
 * Middle slot is always the primary/main action (Rations for farm users).
 * Flanking tabs: 2 left + 2 right.
 * If a role has >4 non-primary tabs, slot [4] becomes "More".
 */
export default function MobileBottomTabBar({
  isCorporate,
  hasSelectedFarm = false,
  onMorePress,
}: MobileBottomTabBarProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();

  // Full farm / admin / consultant tab set — always show these 5
  const defaultTabs: Tab[] = [
    { to: '/dashboard',         label: t('nav.dashboard'),         icon: FaTachometerAlt },
    { to: '/inventory',         label: t('nav.inventory'),         icon: FaWarehouse },
    { to: '/rations',           label: t('nav.rations'),           icon: RationIcon, primary: true },
    { to: '/feeding-calendar',  label: t('nav.feedingShort', { defaultValue: 'Feeding' }), icon: FaCalendarAlt },
    { to: '/records-and-charts',label: t('nav.recordsShort', { defaultValue: 'Records' }), icon: FaChartLine },
  ];

  const corporateOrgTabs: Tab[] = [
    { to: '/corporate-dashboard', label: t('nav.dashboard'), icon: FaBuilding },
    { to: '/farm-management', label: t('nav.farms'), icon: FaSeedling },
    { to: '/corporate/feed-library', label: t('nav.library'), icon: FaList, primary: true },
    { to: '/help', label: t('nav.support'), icon: FaQuestionCircle },
  ];

  const tabs = isCorporate && !hasSelectedFarm ? corporateOrgTabs : defaultTabs;

  // Split into left / primary / right
  const primaryIndex = tabs.findIndex(t => t.primary);
  const hasPrimary = primaryIndex !== -1;

  // Max visible = 5 slots. If more non-primary tabs exist, last slot becomes "More"
  const MAX_SLOTS = 5;
  let slots: (Tab | { more: true })[] = [...tabs];
  if (isCorporate && !hasSelectedFarm) {
    slots = [...corporateOrgTabs, { more: true }];
  } else if (slots.length > MAX_SLOTS) {
    slots = [...tabs.slice(0, MAX_SLOTS - 1), { more: true }];
  }

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="bg-white/85 backdrop-blur-xl border-t border-black/[0.08] supports-[backdrop-filter]:bg-white/75">
        <div className={`flex items-end justify-around px-1 ${hasPrimary ? 'pt-2 pb-1' : 'py-1'}`}>
          {slots.map((slot) => {
            // More button
            if ('more' in slot) {
              return (
                <button
                  key="more"
                  type="button"
                  onClick={onMorePress}
                  className="flex flex-col items-center gap-0.5 px-1 py-1 min-w-0 flex-1 touch-manipulation active:opacity-60"
                >
                  <div className="h-7 w-7 flex items-center justify-center">
                    <FaEllipsisH className="h-4 w-4 text-[#8E8E93]" />
                  </div>
                  <span className="text-[10px] font-medium text-[#8E8E93] leading-none mt-0.5">
                    {t('nav.more', 'More')}
                  </span>
                </button>
              );
            }

            const active = isActive(slot.to);

            // Primary (center) tab — floating pill
            if (slot.primary) {
              return (
                <button
                  key={slot.to}
                  type="button"
                  onClick={() => navigate(slot.to)}
                  className="flex flex-col items-center gap-1 -mt-6 touch-manipulation active:scale-95 transition-transform duration-150"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div
                    className="h-[56px] w-[56px] rounded-[18px] flex items-center justify-center"
                    style={{
                      background: active
                        ? 'linear-gradient(145deg, #0A84FF, #0066CC)'
                        : 'linear-gradient(145deg, #34C759, #28A745)',
                      boxShadow: active
                        ? '0 8px 20px rgba(0,122,255,0.40), 0 2px 6px rgba(0,0,0,0.12)'
                        : '0 8px 20px rgba(52,199,89,0.38), 0 2px 6px rgba(0,0,0,0.10)',
                    }}
                  >
                    <slot.icon className="h-[26px] w-[26px] text-white" />
                  </div>
                  <span
                    className={`text-[10px] font-semibold leading-none mt-0.5 ${
                      active ? 'text-[#007AFF]' : 'text-[#34C759]'
                    }`}
                  >
                    {slot.label}
                  </span>
                </button>
              );
            }

            // Regular tab
            return (
              <button
                key={slot.to}
                type="button"
                onClick={() => navigate(slot.to)}
                className="flex flex-col items-center gap-0.5 px-1 py-1 min-w-0 flex-1 touch-manipulation active:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div
                  className={`h-7 w-7 flex items-center justify-center rounded-[8px] transition-colors duration-150 ${
                    active ? 'text-[#007AFF]' : 'text-[#8E8E93]'
                  }`}
                >
                  <slot.icon className="h-[18px] w-[18px]" />
                </div>
                <span
                  className={`text-[10px] leading-none mt-0.5 text-center whitespace-nowrap transition-colors duration-150 ${
                    active ? 'text-[#007AFF] font-semibold' : 'text-[#8E8E93] font-medium'
                  }`}
                >
                  {slot.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
